import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useHapticFeedback } from "@/hooks/useHapticFeedback";
import { getBadgeById } from "@/lib/badges";
import { BadgeIcon } from "./BadgeIcon";
import confetti from "canvas-confetti";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, Sparkles, Share2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface BadgeUnlockModalProps {
  badgeIds: string[];
  open: boolean;
  onClose: () => void;
  calmMode?: boolean;
}

const RECOVERY_BADGES = ['still_here', 'again'];

const RECOVERY_MESSAGES: Record<string, { title: string; subtitle: string }> = {
  still_here: {
    title: "Welcome back. 🪶",
    subtitle: "We kept your spot warm.",
  },
  again: {
    title: "Look at you — back again.",
    subtitle: "That's the whole thing, really.",
  },
};

export const BadgeUnlockModal = ({
  badgeIds,
  open,
  onClose,
  calmMode = false,
}: BadgeUnlockModalProps) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { success } = useHapticFeedback();
  const { toast } = useToast();
  const [currentIndex, setCurrentIndex] = useState(0);

  const currentBadge = badgeIds[currentIndex] ? getBadgeById(badgeIds[currentIndex]) : null;
  const hasMoreBadges = currentIndex < badgeIds.length - 1;
  const isRecoveryBadge = currentBadge ? RECOVERY_BADGES.includes(currentBadge.id) : false;

  // Confetti + haptic
  useEffect(() => {
    if (!open || !currentBadge) return;
    if (calmMode) return; // No confetti, no haptic in calm mode
    
    success();
    const colors = ['#f59e0b', '#fbbf24', '#fcd34d', '#d97706'];
    setTimeout(() => confetti({ particleCount: 60, spread: 70, origin: { x: 0.5, y: 0.5 }, colors, scalar: 1.2 }), 100);
    setTimeout(() => {
      confetti({ particleCount: 40, angle: 60, spread: 55, origin: { x: 0, y: 0.6 }, colors });
      confetti({ particleCount: 40, angle: 120, spread: 55, origin: { x: 1, y: 0.6 }, colors });
    }, 300);
  }, [open, currentIndex, currentBadge, calmMode, success]);

  // Auto-dismiss — but NOT for recovery badges
  useEffect(() => {
    if (!open || isRecoveryBadge) return;
    const timer = setTimeout(() => {
      if (hasMoreBadges) setCurrentIndex(prev => prev + 1);
      else handleClose();
    }, 5000);
    return () => clearTimeout(timer);
  }, [open, currentIndex, hasMoreBadges, isRecoveryBadge]);

  useEffect(() => { if (open) setCurrentIndex(0); }, [open]);

  const handleClose = () => { setCurrentIndex(0); onClose(); };
  const handleNext = () => { if (hasMoreBadges) setCurrentIndex(prev => prev + 1); else handleClose(); };
  const handleViewAll = () => { handleClose(); navigate('/badges'); };

  const handleShare = async () => {
    if (!currentBadge) return;
    const shareText = `Just earned '${currentBadge.name}' on Wren — the expense tracker that actually works for my brain. trywren.app`;
    try {
      await navigator.clipboard.writeText(shareText);
      toast({ title: "Copied to clipboard!", description: "Share it with friends 🎉" });
    } catch {
      toast({ title: "Couldn't copy", variant: "destructive" });
    }
  };

  if (!currentBadge) return null;

  const recoveryMessage = isRecoveryBadge ? RECOVERY_MESSAGES[currentBadge.id] : null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent
        className={cn(
          "sm:max-w-md border-0 overflow-hidden",
          isRecoveryBadge ? "bg-gradient-to-b from-green-50 to-background dark:from-green-950/20 dark:to-background" : "bg-gradient-to-b from-background to-muted/50"
        )}
        hideCloseButton
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentBadge.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={calmMode ? { duration: 0.3 } : undefined}
            className="flex flex-col items-center py-6 px-4 text-center"
          >
            {!isRecoveryBadge && (
              <motion.div initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1, type: "spring" }} className="mb-2">
                <Sparkles className="w-6 h-6 text-amber-500" />
              </motion.div>
            )}

            <motion.h2 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className={cn("text-lg font-semibold mb-4",
                isRecoveryBadge ? "text-green-700 dark:text-green-400" : "text-amber-600 dark:text-amber-400")}>
              {recoveryMessage?.title ?? (t('badges.badgeUnlocked') || 'Badge Unlocked!')}
            </motion.h2>

            <motion.div
              initial={calmMode ? { opacity: 0 } : { scale: 0, rotate: -180 }}
              animate={calmMode ? { opacity: 1 } : { scale: 1, rotate: 0 }}
              transition={{ delay: 0.2, type: calmMode ? "tween" : "spring", stiffness: 200, damping: 15 }}
              className="relative mb-6"
            >
              {!calmMode && <div className="absolute inset-0 bg-amber-400/30 blur-xl rounded-full scale-150" />}
              <div className="relative">
                <BadgeIcon iconName={currentBadge.iconName} size="xl" />
              </div>
            </motion.div>

            <motion.h3 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }} className="text-xl font-bold mb-2">
              {currentBadge.name}
            </motion.h3>

            <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }} className="text-muted-foreground text-sm mb-6 max-w-xs">
              {recoveryMessage?.subtitle ?? currentBadge.description}
            </motion.p>

            {badgeIds.length > 1 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
                className="flex items-center gap-1 mb-4">
                {badgeIds.map((_, idx) => (
                  <div key={idx} className={`w-2 h-2 rounded-full transition-colors ${
                    idx === currentIndex ? 'bg-amber-500' : idx < currentIndex ? 'bg-amber-300' : 'bg-muted-foreground/30'
                  }`} />
                ))}
              </motion.div>
            )}

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }} className="flex flex-col gap-2 w-full max-w-xs">
              <Button onClick={handleNext}
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold"
                size="lg">
                {hasMoreBadges ? (t('badges.nextBadge') || 'Next Badge') : (t('badges.awesome') || 'Continue')}
                {hasMoreBadges && <ChevronRight className="w-4 h-4 ml-1" />}
              </Button>

              {currentBadge.shareable && !calmMode && (
                <Button variant="outline" size="sm" onClick={handleShare} className="gap-2">
                  <Share2 className="w-4 h-4" /> Share
                </Button>
              )}

              {badgeIds.length > 1 && (
                <Button variant="ghost" onClick={handleViewAll}
                  className="text-muted-foreground hover:text-foreground" size="sm">
                  {t('badges.viewAll') || 'View All Badges'}
                </Button>
              )}
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

